import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import Layout from "@/components/Layout";
import TimeBadge from "@/components/TimeBadge";
import { CAUSE_NAMES } from "@/lib/causes";
import { CheckCircle2 } from "lucide-react";

const STATUS_LABELS = {
  signed_up: "Signed up",
  completed_pending: "Awaiting verification",
  credited: "Credited",
  rejected: "Not verified",
};

const STATUS_STYLES = {
  signed_up: "bg-blue-50 text-blue-800 border-blue-200",
  completed_pending: "bg-amber-50 text-amber-800 border-amber-200",
  credited: "bg-emerald-50 text-emerald-800 border-emerald-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
};

export default function MyVolunteering() {
  const [signups, setSignups] = useState([]);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");

  const load = () => api.get("/me/signups").then(({ data }) => setSignups(data));
  useEffect(() => { load(); }, []);

  const markComplete = async (id) => {
    setBusy(id);
    setErr("");
    try {
      await api.post("/opportunities/mark-complete", { opportunity_id: id });
      await load();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy("");
    }
  };

  const filtered = filter === "all" ? signups : signups.filter((s) => s.status === filter);

  return (
    <Layout>
      <div data-testid="my-volunteering-page" className="max-w-5xl mx-auto px-5 md:px-8 py-12">
        <span className="overline text-[var(--cm-primary)]">My volunteering</span>
        <h1 className="font-display font-bold text-4xl md:text-5xl mt-2 tracking-tight">Your shifts.</h1>
        <p className="text-ink-soft mt-2 max-w-xl">Sign up, show up, mark complete — your organizer credits your hours.</p>

        <div className="flex flex-wrap gap-2 mt-8">
          {[
            ["all", "All"],
            ["signed_up", "Upcoming"],
            ["completed_pending", "Awaiting verification"],
            ["credited", "Credited"],
            ["rejected", "Not verified"],
          ].map(([k, label]) => (
            <button
              key={k}
              data-testid={`mv-filter-${k}`}
              onClick={() => setFilter(k)}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
                filter === k ? "bg-[var(--cm-ink)] text-white border-[var(--cm-ink)]" : "border-ink-soft hover:border-[var(--cm-primary)]"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {err && <div className="mt-6 text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-2 rounded-lg">{err}</div>}

        <div className="mt-8 space-y-4">
          {filtered.length === 0 ? (
            <div className="card-soft p-12 text-center" data-testid="mv-empty">
              <p className="text-ink-soft">Nothing here yet.</p>
              <Link to="/opportunities" className="btn-primary mt-4 inline-block text-sm">Browse opportunities</Link>
            </div>
          ) : (
            filtered.map((s) => (
              <div key={s.id} className="card-soft p-6 flex flex-wrap gap-4 items-center justify-between" data-testid={`mv-signup-${s.id}`}>
                <div className="flex-1 min-w-[200px]">
                  <h3 className="font-display font-semibold text-lg">{s.opportunity_title}</h3>
                  <p className="text-xs text-ink-soft mt-1">
                    {CAUSE_NAMES[s.opportunity_cause]} · with {s.organizer_name}
                  </p>
                  {s.organizer_note && (
                    <p className="text-xs text-ink-soft mt-2 italic">"{s.organizer_note}"</p>
                  )}
                </div>
                <TimeBadge hours={s.hours_credited > 0 ? s.hours_credited : s.opportunity_hours} size="sm" />
                <span className={`pill ${STATUS_STYLES[s.status]}`} data-testid={`mv-status-${s.id}`}>{STATUS_LABELS[s.status]}</span>
                {s.status === "signed_up" && (
                  <button
                    data-testid={`mv-mark-complete-${s.id}`}
                    onClick={() => markComplete(s.id)}
                    disabled={busy === s.id}
                    className="btn-primary text-sm py-1.5 px-4 inline-flex items-center gap-1.5 disabled:opacity-60"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" /> {busy === s.id ? "…" : "Mark complete"}
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </Layout>
  );
}
